/**
 * OrphanContactAlert — BDR Daily Surface (W10, 2026-05-27)
 *
 * Surfaces contacts that have no account_id for the current tenant. Each
 * contact is shown as a chip with a "Link to account" affordance (stub toast
 * — full linking workflow is out of scope for this wave).
 *
 * Placement: top of BdrView Daily Surface, above the queue. Only renders when
 * orphan count > 0 and the rep has not dismissed it today.
 *
 * Canon rules: no emoji, no exclamation, no italics, M-PACT hyphenated.
 */
import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";
import { tokens } from "@/components/operator/tokens";
import { useToast } from "@/hooks/use-toast";

interface OrphanContact {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  created_at: string;
}

const DISMISS_KEY = "aip:orphan-alert-dismissed-until";

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() < Number(raw);
  } catch {
    return false;
  }
}

function dismissForToday() {
  try {
    // Expires at midnight local time
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
    localStorage.setItem(DISMISS_KEY, String(midnight));
  } catch {
    // ignore
  }
}

function fullName(c: OrphanContact): string {
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : `Contact #${c.id}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function OrphanContactAlert() {
  const tenant = getCurrentTenant();
  const tenantId = tenant?.id ?? 1;
  const { toast } = useToast();

  const [contacts, setContacts] = useState<OrphanContact[]>([]);
  const [dismissed, setDismissed] = useState<boolean>(isDismissed);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();
        const { data, error } = await sb
          .from("contacts")
          .select("id, first_name, last_name, email, created_at")
          .eq("tenant_id", tenantId)
          .is("account_id", null)
          .order("created_at", { ascending: false })
          .limit(10);
        if (cancelled) return;
        if (!error && data) {
          setContacts(data as OrphanContact[]);
        }
      } catch {
        // non-fatal — alert simply does not render
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // Re-check dismissal state on mount in case another tab dismissed
  useEffect(() => {
    setDismissed(isDismissed());
  }, []);

  if (loading || dismissed || contacts.length === 0) return null;

  const canDismiss = contacts.length < 3;

  function handleDismiss() {
    dismissForToday();
    setDismissed(true);
  }

  function handleChipClick(c: OrphanContact) {
    toast({
      title: "Link to account",
      description: `Open the edit form for ${fullName(c)} and assign an account. Full linking workflow ships in a future wave.`,
      duration: 4000,
    });
  }

  return (
    <div
      data-testid="orphan-contact-alert"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 16px",
        background: "#F4F4F0",
        borderLeft: "4px solid #D9534F",
        borderRadius: 6,
        marginBottom: 16,
        minHeight: 56,
        flexWrap: "wrap",
      }}
    >
      {/* Warning icon */}
      <svg
        width={20}
        height={20}
        viewBox="0 0 20 20"
        fill="none"
        style={{ flexShrink: 0, marginTop: 2 }}
        aria-hidden
      >
        <path
          d="M8.485 3.215a1.75 1.75 0 0 1 3.03 0l6.3 10.89A1.75 1.75 0 0 1 16.3 17H3.7a1.75 1.75 0 0 1-1.515-2.895l6.3-10.89Z"
          fill="#D9534F"
        />
        <path d="M10 7.5v3.5" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" />
        <circle cx={10} cy={13} r={0.875} fill="#fff" />
      </svg>

      {/* Text + chips */}
      <div style={{ flex: 1, minWidth: 200 }}>
        <div
          style={{
            fontFamily: tokens.font.body,
            fontWeight: 600,
            fontSize: 15,
            color: "#1A1A1A",
            marginBottom: 2,
          }}
        >
          {contacts.length === 1
            ? "1 contact is not linked to an account."
            : `${contacts.length} contacts are not linked to an account.`}
        </div>
        <div
          style={{
            fontFamily: tokens.font.body,
            fontSize: 13,
            color: "#4A4A4A",
            marginBottom: 8,
          }}
        >
          Every contact lives under an account. Click a name to link it — or use
          the contact edit form to assign one.
        </div>

        {/* Chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {contacts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleChipClick(c)}
              title={`Created ${timeAgo(c.created_at)}${c.email ? ` · ${c.email}` : ""}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#FFFFFF",
                border: "1px solid #D9534F",
                borderRadius: 4,
                padding: "3px 10px",
                fontSize: 12,
                fontFamily: tokens.font.body,
                color: "#1A1A1A",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {fullName(c)}
              <span style={{ color: "#D9534F", fontSize: 11, fontWeight: 600 }}>
                Link to account
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Dismiss */}
      {canDismiss && (
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            flexShrink: 0,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            color: tokens.color.textMuted,
            fontFamily: tokens.font.body,
            padding: "2px 0",
            alignSelf: "flex-start",
            marginTop: 2,
          }}
        >
          Dismiss for today
        </button>
      )}
    </div>
  );
}
